import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { formatLocal, relativeTime } from '../utils/time'

type Kind = 'feeds' | 'lists' | 'packs'

const TITLES: Record<Kind, string> = {
  feeds: 'feed generators this account publishes',
  lists: 'lists this account publishes (mod / curate / reference)',
  packs: 'starter packs this account publishes',
}

export function PublicView({ handle, kind }: { handle: string; kind: Kind }) {
  const [items, setItems] = useState<Array<Record<string, unknown>>>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function loadPage(reset: boolean) {
    setLoading(true)
    setError(null)
    try {
      const c = reset ? undefined : cursor ?? undefined
      let list: Array<Record<string, unknown>> = []
      let nextCursor: string | null = null
      if (kind === 'feeds') {
        const r = await api.actorFeeds(handle, c)
        list = r.feeds
        nextCursor = r.cursor
      } else if (kind === 'lists') {
        const r = await api.actorLists(handle, c)
        list = r.lists
        nextCursor = r.cursor
      } else {
        const r = await api.actorStarterPacks(handle, c)
        list = r.packs
        nextCursor = r.cursor
      }
      setItems((prev) => (reset ? list : [...prev, ...list]))
      setCursor(nextCursor)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setItems([])
    setCursor(null)
    loadPage(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, kind])

  return (
    <div className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass mb-3 px-4 py-2 text-xs text-ink-300">
        <strong className="text-white">{items.length.toLocaleString()}</strong> {kind} •{' '}
        <span className="text-ink-400">{TITLES[kind]}</span>
        {error ? <div className="mt-1 text-rose-200">{error}</div> : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((it, i) => {
          if (kind === 'feeds') return <FeedTile key={String(it.uri ?? i)} feed={it} />
          if (kind === 'lists') return <ListTile key={String(it.uri ?? i)} list={it} />
          return <PackTile key={String(it.uri ?? i)} pack={it} />
        })}
      </div>

      <div className="mt-4 flex justify-center">
        {cursor ? (
          <button
            type="button"
            className="glass-button glass-button--accent"
            onClick={() => loadPage(false)}
            disabled={loading}
          >
            {loading ? 'loading…' : 'load more'}
          </button>
        ) : (
          <span className="text-xs text-ink-400">
            {loading ? 'loading…' : items.length === 0 ? 'nothing to show' : 'end of list'}
          </span>
        )}
      </div>
    </div>
  )
}

function getStr(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key]
  return typeof v === 'string' ? v : undefined
}

function getNum(o: Record<string, unknown>, key: string): number | undefined {
  const v = o[key]
  return typeof v === 'number' ? v : undefined
}

function getCreator(o: Record<string, unknown>): { did: string; handle?: string } | null {
  const c = o.creator as Record<string, unknown> | undefined
  if (!c) return null
  const did = typeof c.did === 'string' ? c.did : ''
  if (!did) return null
  return { did, handle: typeof c.handle === 'string' ? c.handle : undefined }
}

function FeedTile({ feed }: { feed: Record<string, unknown> }) {
  const display = getStr(feed, 'displayName') ?? '(no name)'
  const description = getStr(feed, 'description')
  const avatar = getStr(feed, 'avatar')
  const likes = getNum(feed, 'likeCount')
  const did = (() => {
    const u = getStr(feed, 'uri')
    if (!u) return null
    return u.split('/')[2] ?? null
  })()
  const rkey = (() => {
    const u = getStr(feed, 'uri')
    if (!u) return null
    return u.split('/').pop() ?? null
  })()
  const indexedAt = getStr(feed, 'indexedAt')
  return (
    <div className="glass flex items-start gap-3 p-4">
      {avatar ? (
        <img src={avatar} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-emerald-500/40 to-sky-500/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="truncate text-sm font-bold text-white">{display}</span>
          <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-200">
            feed
          </span>
          {typeof likes === 'number' ? (
            <span className="text-[10px] text-ink-300">{likes.toLocaleString()} likes</span>
          ) : null}
        </div>
        {description ? (
          <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{description}</p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
          {indexedAt ? (
            <span title={formatLocal(new Date(indexedAt))}>indexed {relativeTime(new Date(indexedAt))}</span>
          ) : null}
          {did && rkey ? (
            <a
              href={`https://bsky.app/profile/${did}/feed/${rkey}`}
              target="_blank"
              rel="noreferrer"
              className="accent-text hover:underline"
            >
              open feed ↗
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function ListTile({ list }: { list: Record<string, unknown> }) {
  const name = getStr(list, 'name') ?? '(unnamed)'
  const description = getStr(list, 'description')
  const avatar = getStr(list, 'avatar')
  const purpose = (getStr(list, 'purpose') ?? '').replace('app.bsky.graph.defs#', '')
  const itemCount = getNum(list, 'listItemCount')
  const creator = getCreator(list)
  const u = getStr(list, 'uri')
  const rkey = u?.split('/').pop() ?? null
  return (
    <div className="glass flex items-start gap-3 p-4">
      {avatar ? (
        <img src={avatar} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="truncate text-sm font-bold text-white">{name}</span>
          {purpose ? (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-ink-200">
              {purpose}
            </span>
          ) : null}
          {typeof itemCount === 'number' ? (
            <span className="text-[10px] text-ink-300">{itemCount.toLocaleString()} items</span>
          ) : null}
        </div>
        {description ? (
          <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{description}</p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
          {creator ? <span className="truncate font-mono">@{creator.handle ?? creator.did}</span> : null}
          {creator && rkey ? (
            <a
              href={`https://bsky.app/profile/${creator.did}/lists/${rkey}`}
              target="_blank"
              rel="noreferrer"
              className="accent-text hover:underline"
            >
              open list ↗
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function PackTile({ pack }: { pack: Record<string, unknown> }) {
  const recordOuter = pack.record as Record<string, unknown> | undefined
  const name = getStr(recordOuter ?? {}, 'name') ?? '(unnamed)'
  const description = getStr(recordOuter ?? {}, 'description')
  const joined = getNum(pack, 'joinedAllTimeCount')
  const items = getNum(pack, 'listItemCount')
  const creator = getCreator(pack)
  const u = getStr(pack, 'uri')
  const rkey = u?.split('/').pop() ?? null
  return (
    <div className="glass p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-bold text-white">{name}</span>
        <span className="rounded-full border border-violet-400/30 bg-violet-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-200">
          starter pack
        </span>
        {typeof items === 'number' ? (
          <span className="text-[10px] text-ink-300">{items.toLocaleString()} accounts</span>
        ) : null}
        {typeof joined === 'number' ? (
          <span className="text-[10px] text-ink-300">{joined.toLocaleString()} joined via</span>
        ) : null}
      </div>
      {description ? (
        <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{description}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
        {creator ? <span className="truncate font-mono">@{creator.handle ?? creator.did}</span> : null}
        {creator && rkey ? (
          <a
            href={`https://bsky.app/starter-pack/${creator.did}/${rkey}`}
            target="_blank"
            rel="noreferrer"
            className="accent-text hover:underline"
          >
            open pack ↗
          </a>
        ) : null}
      </div>
    </div>
  )
}
